import React, { createContext, useContext, useReducer } from 'react';

export type MainOptionId =
  | 'campaigns'
  | 'dealManagerModal'
  | 'pixels'
  | 'contextSystem'
  | 'insights'
  | 'assignDealsModal'
  | 'deviatedLineItems';
export type SubOptionId = 'newInsights' | 'businessInsights';

type MainMenuSelectionType = {
  mainItemSelected: MainOptionId | null;
};

type Status = 'show' | 'hide';

type MenuState = {
  aliasModal: Status;
  changeUserModal: Status;
  dealsManagerModal: Status;
  notificationSettingsModal: Status;
  myProfileModal: Status;
  mainItemSelected: MainMenuSelectionType['mainItemSelected'];
};

type SetStateFn = (state: Status) => void;
type SetMainItemFn = (item: MenuState['mainItemSelected']) => void;

type ActionType =
  | 'SHOW_ALIAS_MODAL'
  | 'SHOW_CHANGE_USER_MODAL'
  | 'SHOW_NOTIFICATION_SETTINGS_MODAL'
  | 'SHOW_MY_PROFILE_MODAL'
  | 'SELECT_MENU_ITEM'
  | 'SHOW_DEALS_MANAGER_MODAL';

type Action = {
  type: ActionType;
  value: Status | MainMenuSelectionType;
};

const modalReducer = (state: MenuState, action: Action): MenuState => {
  switch (action.type) {
    case 'SHOW_ALIAS_MODAL':
      return {
        ...state,
        aliasModal: action.value as Status
      };
    case 'SHOW_CHANGE_USER_MODAL':
      return {
        ...state,
        changeUserModal: action.value as Status
      };
    case 'SHOW_NOTIFICATION_SETTINGS_MODAL':
      return {
        ...state,
        notificationSettingsModal: action.value as Status
      };
    case 'SHOW_MY_PROFILE_MODAL':
      return {
        ...state,
        myProfileModal: action.value as Status
      };
    case 'SHOW_DEALS_MANAGER_MODAL':
      return {
        ...state,
        dealsManagerModal: action.value as Status
      };
    case 'SELECT_MENU_ITEM':
      return {
        ...state,
        mainItemSelected: (action.value as MainMenuSelectionType).mainItemSelected || 'campaigns'
      };
    default:
      return state;
  }
};

type Context = {
  setMenuItemSelected: SetMainItemFn;
  showAliasModal: SetStateFn;
  showChangeUserModal: SetStateFn;
  showMyProfileModal: SetStateFn;
  showNotificationSettingsModal: SetStateFn;
  state: MenuState;
};

const MenuContext = createContext<Context | null>(null);

const initialState: MenuState = {
  aliasModal: 'hide' as const,
  changeUserModal: 'hide' as const,
  dealsManagerModal: 'hide' as const,
  mainItemSelected: 'campaigns',
  myProfileModal: 'hide' as const,
  notificationSettingsModal: 'hide' as const
};

function MenuProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(modalReducer, initialState);

  const showAliasModal = (value: Status): void =>
    dispatch({
      type: 'SHOW_ALIAS_MODAL',
      value
    });

  const showChangeUserModal = (value: Status): void =>
    dispatch({
      type: 'SHOW_CHANGE_USER_MODAL',
      value
    });

  const showNotificationSettingsModal = (value: Status): void =>
    dispatch({
      type: 'SHOW_NOTIFICATION_SETTINGS_MODAL',
      value
    });

  const showMyProfileModal = (value: Status): void =>
    dispatch({
      type: 'SHOW_MY_PROFILE_MODAL',
      value
    });

  const setMenuItemSelected = (value: MainMenuSelectionType['mainItemSelected']): void => {
    dispatch({
      type: 'SELECT_MENU_ITEM',
      value: { mainItemSelected: value }
    });
  };

  return (
    <MenuContext.Provider
      value={{
        setMenuItemSelected,
        showAliasModal,
        showChangeUserModal,
        showMyProfileModal,
        showNotificationSettingsModal,
        state
      }}
    >
      {children}
    </MenuContext.Provider>
  );
}

function useMenu(): Context {
  const context = useContext(MenuContext);

  if (context === undefined) {
    throw new Error('useMenu must be used within a MenuProvider');
  }

  return context as Context;
}

export { MenuProvider, useMenu };
